import { ComponentType, useEffect, useState } from 'react';

import { PREDECESSOR_LOCATION_LABEL } from 'components/insights/constants';
import { END_DATE, START_DATE } from 'constants/filterKeysConstants';
import { useProfile } from 'context/profile';
import {
  NetworkPerformanceMetricDrillKey,
  useNetworkPerformanceActions,
  useNetworkPerformanceStore,
} from 'stores/networkPerformanceStore';

import SlideoutShell, { BorderedSection } from '../../shared/slideout/SlideoutShell';

import AvgStartOfCareHeader from './headers/AvgStartOfCareHeader';
import EngagementsHeader from './headers/EngagementsHeader';
import OesHeader from './headers/OesHeader';
import ReadmissionRateHeader from './headers/ReadmissionRateHeader';
import SafeReturnsHeader from './headers/SafeReturnsHeader';
import ServiceRefusalsHeader from './headers/ServiceRefusalsHeader';
import { METRIC_CONFIGS, NetworkPerformanceMetrics } from './constants';
import NetworkPerformanceBreakdownTable from './NetworkPerformanceBreakdownTable';
import NetworkPerformancePatientList from './NetworkPerformancePatientList';
import useNetworkPerformanceSlideoutAnalytics, {
  NetworkPerformanceSlideoutAnalyticsResult,
} from './useNetworkPerformanceSlideoutAnalytics';

export type MetricHeaderProps = NetworkPerformanceSlideoutAnalyticsResult & {
  entityLabel: string;
};

const METRIC_HEADERS: Record<NetworkPerformanceMetricDrillKey, ComponentType<MetricHeaderProps>> = {
  [NetworkPerformanceMetrics.AVG_START_OF_CARE]: AvgStartOfCareHeader,
  [NetworkPerformanceMetrics.SERVICE_REFUSALS]: ServiceRefusalsHeader,
  [NetworkPerformanceMetrics.SAFE_RETURNS]: SafeReturnsHeader,
  [NetworkPerformanceMetrics.OES]: OesHeader,
  [NetworkPerformanceMetrics.READMISSION_RATE]: ReadmissionRateHeader,
  [NetworkPerformanceMetrics.ENGAGEMENTS]: EngagementsHeader,
};

type SlideoutMeta = {
  title: string;
  subtitle: string;
  ready: boolean;
};

export default function NetworkPerformanceSlideout() {
  const slideoutContext = useNetworkPerformanceStore((s) => s.slideoutContext);
  const { closeSlideout } = useNetworkPerformanceActions();
  const startDate = useNetworkPerformanceStore((s) => s.filters[START_DATE]) as Date | undefined;
  const endDate = useNetworkPerformanceStore((s) => s.filters[END_DATE]) as Date | undefined;

  const [meta, setMeta] = useState<SlideoutMeta>({ title: '', subtitle: '', ready: false });

  const metricContext = slideoutContext?.kind === 'metric' ? slideoutContext : null;

  return (
    <SlideoutShell
      open={!!metricContext}
      onClose={closeSlideout}
      title={meta.title}
      subtitle={meta.subtitle}
      subtitleReady={meta.ready}
      startDate={startDate}
      endDate={endDate}>
      {metricContext && <SlideoutContent metric={metricContext.metric} onMeta={setMeta} />}
    </SlideoutShell>
  );
}

function SlideoutContent({
  metric,
  onMeta,
}: {
  metric: NetworkPerformanceMetricDrillKey;
  onMeta: (m: SlideoutMeta) => void;
}) {
  const { profile } = useProfile();
  const selectedGroupType = useNetworkPerformanceStore((s) => s.selectedGroupType);

  const analytics = useNetworkPerformanceSlideoutAnalytics(metric);

  const isEngagements = metric === NetworkPerformanceMetrics.ENGAGEMENTS;
  const receivingLabel = `${selectedGroupType!.displayName}s`;
  const entityLabel = isEngagements ? PREDECESSOR_LOCATION_LABEL : `${selectedGroupType!.displayName} Company`;

  const metricLabel = METRIC_CONFIGS[metric].label;
  const titlePrefix = isEngagements ? `${profile.actingClient.name} ` : '';
  const title = `${titlePrefix}${metricLabel} - Overview`;
  const subtitle = `${analytics.predecessorCount} ${PREDECESSOR_LOCATION_LABEL}s \u2022 ${analytics.receivingGroupCount} ${receivingLabel}`;
  const ready = !analytics.loading && !analytics.countsLoading;

  useEffect(() => {
    onMeta({ title, subtitle, ready });
  }, [title, subtitle, ready, onMeta]);

  const HeaderComponent = METRIC_HEADERS[metric];

  return (
    <>
      <HeaderComponent {...analytics} entityLabel={entityLabel} />
      <BorderedSection>
        <NetworkPerformanceBreakdownTable metric={metric} loading={analytics.loading} rollups={analytics.rollups} />
      </BorderedSection>
      <NetworkPerformancePatientList metric={metric} />
    </>
  );
}
